import { Dialog } from "@/components/ui/dialog"
import { useLocation } from "react-router-dom"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { OfferModal } from "./offer-modal"
import { MvpCalculatorModal } from "./mvp-calculator-modal"

const DISMISS_STORAGE_KEY = "owm_modal_dismissed_types"
const SUPPRESS_STORAGE_KEY = "owm_modal_suppressed"
const CALCULATOR_SEEN_KEY = "owm_calculator_modal_seen"

export type ModalType = "mvp-offer" | "mvp-calculator"

interface ModalState {
  type: ModalType
}

interface ModalContextValue {
  activeModal: ModalState | null
  openModal: (type: ModalType) => void
  closeModal: () => void
  markDismissed: (type: ModalType) => void
  hasDismissed: (type: ModalType) => boolean
  suppressAll: (reason?: string) => void
  isSuppressed: boolean
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined)

interface ModalProviderProps {
  children: React.ReactNode
}

function readDismissedTypes(): Set<ModalType> {
  if (typeof window === "undefined") return new Set()
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as ModalType[]
    return new Set(parsed)
  } catch (error) {
    return new Set()
  }
}

function persistDismissedTypes(types: Set<ModalType>) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(Array.from(types)))
  } catch (error) {
    // Silent fail for localStorage issues
  }
}

function readSuppressed(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(SUPPRESS_STORAGE_KEY) === "true"
  } catch (error) {
    return false
  }
}

function writeSuppressed(value: boolean) {
  if (typeof window === "undefined") return
  try {
    if (value) {
      window.localStorage.setItem(SUPPRESS_STORAGE_KEY, "true")
    } else {
      window.localStorage.removeItem(SUPPRESS_STORAGE_KEY)
    }
  } catch (error) {
    // Silent fail for localStorage issues
  }
}

function readCalculatorSeen(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(CALCULATOR_SEEN_KEY) === "true"
  } catch (error) {
    return false
  }
}

function writeCalculatorSeen(value: boolean) {
  if (typeof window === "undefined") return
  try {
    if (value) {
      window.localStorage.setItem(CALCULATOR_SEEN_KEY, "true")
    } else {
      window.localStorage.removeItem(CALCULATOR_SEEN_KEY)
    }
  } catch (error) {
    // Silent fail for localStorage issues
  }
}

export function ModalProvider({ children }: ModalProviderProps) {
  const location = useLocation()
  const [activeModal, setActiveModal] = useState<ModalState | null>(null)
  const [dismissedTypes, setDismissedTypes] = useState<Set<ModalType>>(readDismissedTypes)
  const [isSuppressed, setIsSuppressed] = useState<boolean>(readSuppressed)
  const hasLinkClickRef = useRef<boolean>(false)
  const hasTriggeredOfferRef = useRef<boolean>(false)
  const scrollListenerRef = useRef<() => void>()
  const pageDepthRef = useRef<number>(1)
  const isFirstLoadRef = useRef<boolean>(true)
  const timeTriggerRef = useRef<number | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const link = target.closest("a")
      if (!link) return
      hasLinkClickRef.current = true
      setIsSuppressed(true)
      writeSuppressed(true)
    }

    window.addEventListener("click", handleLinkClick, { capture: true })
    return () => window.removeEventListener("click", handleLinkClick, { capture: true })
  }, [])

  const markDismissed = useCallback((type: ModalType) => {
    setDismissedTypes(previous => {
      if (previous.has(type)) return previous
      const next = new Set(previous)
      next.add(type)
      persistDismissedTypes(next)
      return next
    })
  }, [])

  const hasDismissed = useCallback(
    (type: ModalType) => dismissedTypes.has(type),
    [dismissedTypes],
  )

  const suppressAll = useCallback((reason?: string) => {
    if (reason) console.info("[ModalProvider] Suppressing modals due to", reason)
    setIsSuppressed(true)
    writeSuppressed(true)
  }, [])

  const openModal = useCallback(
    (type: ModalType) => {
      if (isSuppressed) return
      if (hasDismissed(type)) return
      setActiveModal({ type })
    },
    [hasDismissed, isSuppressed],
  )

  const closeModal = useCallback(() => {
    if (!activeModal) return
    markDismissed(activeModal.type)
    setActiveModal(null)
  }, [activeModal, markDismissed])

  useEffect(() => {
    if (activeModal?.type === "mvp-calculator") writeCalculatorSeen(true)
  }, [activeModal])

  useEffect(() => {
    if (isSuppressed) return
    if (hasDismissed("mvp-offer")) return

    const handleOfferDisplay = (trigger: "scroll" | "time" | "depth") => {
      if (hasLinkClickRef.current) return
      if (hasTriggeredOfferRef.current) return
      hasTriggeredOfferRef.current = true
      openModal("mvp-offer")
      if (scrollListenerRef.current) {
        window.removeEventListener("scroll", scrollListenerRef.current)
        scrollListenerRef.current = undefined
      }
      if (timeTriggerRef.current) {
        window.clearTimeout(timeTriggerRef.current)
        timeTriggerRef.current = null
      }
      console.info("[ModalProvider] Offer modal opened via", trigger)
    }

    const handleScroll = () => {
      const documentElement = document.documentElement
      const scrollHeight = documentElement.scrollHeight - window.innerHeight
      if (scrollHeight <= 0) return
      const scrolled = window.scrollY
      if (scrolled / scrollHeight >= 0.8) handleOfferDisplay("scroll")
    }

    if (!scrollListenerRef.current && typeof window !== "undefined") {
      scrollListenerRef.current = handleScroll
      window.addEventListener("scroll", handleScroll, { passive: true })
    }

    if (!timeTriggerRef.current && typeof window !== "undefined") {
      timeTriggerRef.current = window.setTimeout(() => handleOfferDisplay("time"), 45000)
    }

    return () => {
      if (scrollListenerRef.current && typeof window !== "undefined") {
        window.removeEventListener("scroll", scrollListenerRef.current)
        scrollListenerRef.current = undefined
      }
      if (timeTriggerRef.current && typeof window !== "undefined") {
        window.clearTimeout(timeTriggerRef.current)
        timeTriggerRef.current = null
      }
    }
  }, [hasDismissed, isSuppressed, openModal])

  useEffect(() => {
    if (isSuppressed) return
    if (hasDismissed("mvp-offer")) return
    if (hasTriggeredOfferRef.current) return
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false
      return
    }
    pageDepthRef.current += 1
    if (pageDepthRef.current >= 3) {
      hasTriggeredOfferRef.current = true
      openModal("mvp-offer")
    }
  }, [location, hasDismissed, isSuppressed, openModal])

  useEffect(() => {
    if (location.pathname !== "/one-pagers") return
    if (isSuppressed) return
    if (hasDismissed("mvp-calculator")) return
    if (readCalculatorSeen()) return

    const timer = window.setTimeout(() => {
      openModal("mvp-calculator")
    }, 1500)

    return () => window.clearTimeout(timer)
  }, [location.pathname, hasDismissed, isSuppressed, openModal])

  const contextValue = useMemo(
    () => ({
      activeModal,
      openModal,
      closeModal,
      markDismissed,
      hasDismissed,
      suppressAll,
      isSuppressed,
    }),
    [activeModal, closeModal, hasDismissed, markDismissed, openModal, suppressAll, isSuppressed],
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <Dialog open={activeModal?.type === "mvp-offer"} onOpenChange={(open) => (open ? openModal("mvp-offer") : closeModal())}>
        <OfferModal
          isOpen={activeModal?.type === "mvp-offer"}
          onClose={closeModal}
          onDismiss={() => markDismissed("mvp-offer")}
          suppressAll={suppressAll}
        />
      </Dialog>
      <Dialog open={activeModal?.type === "mvp-calculator"} onOpenChange={(open) => (open ? openModal("mvp-calculator") : closeModal())}>
        <MvpCalculatorModal
          isOpen={activeModal?.type === "mvp-calculator"}
          onClose={closeModal}
          onDismiss={() => markDismissed("mvp-calculator")}
        />
      </Dialog>
    </ModalContext.Provider>
  )
}

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) throw new Error("useModal must be used within ModalProvider")
  return context
}

